export type ToastVariant = 'default' | 'success' | 'error' | 'warning'

export type ToastPayload = {
  id: string
  title?: string
  description?: string
  variant: ToastVariant
  duration: number
}

export type ToastInput = Omit<ToastPayload, 'id'> & { id?: string }

type Listener = (toast: ToastPayload) => void

const listeners = new Set<Listener>()

export function onToast(listener: Listener): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

function makeId(): string {
  return typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : String(Date.now() + Math.random())
}

export function toast(input: ToastInput): string {
  const payload: ToastPayload = {
    id: input.id ?? makeId(),
    title: input.title,
    description: input.description,
    variant: input.variant ?? 'default',
    duration: input.duration ?? 3000,
  }

  for (const l of listeners) l(payload)
  return payload.id
}

toast.success = (title: string, description?: string, opts?: Partial<ToastInput>) =>
  toast({ title, description, variant: 'success', ...opts })

toast.error = (title: string, description?: string, opts?: Partial<ToastInput>) =>
  toast({ title, description, variant: 'error', ...opts })

toast.warning = (title: string, description?: string, opts?: Partial<ToastInput>) =>
  toast({ title, description, variant: 'warning', ...opts })
